import React, { createContext, useContext, useState, useEffect } from 'react';
import api from 'connections/api';
import routes_api from 'connections/routes_api';
import { fetchUserCurrencies } from './flow_requests';

const FinanceProfileContext = createContext();

export const useFinanceProfile = () => useContext(FinanceProfileContext);

export const FinanceProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCurrencies = async () => {
    try {
      const list = await fetchUserCurrencies();
      setCurrencies(Array.isArray(list) ? list : []);
    } catch {
      setCurrencies([]);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await api.get(routes_api.finance_profile);
        setProfile(res.data);
        await loadCurrencies();
      } catch (err) {
        setProfile(null);
        setCurrencies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const updateProfile = async (patchData) => {
    try {
      const res = await api.patch(routes_api.finance_profile, patchData);
      setProfile(res.data);
      return res.data;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Finance profile not found. Please contact support to set up your finance profile.');
      }
      throw error;
    }
  };

  /** Options for DropdownCurrency: [{ value, label, symbol }]. Fallback to BRL/USD/EUR if no currencies. */
  const currencyOptions = currencies.length > 0
    ? currencies.filter(c => c.is_active !== false).map(c => ({
        value: c.currency_code,
        label: c.currency_code,
        symbol: c.currency_symbol,
      }))
    : [
        { value: 'BRL', label: 'BRL', symbol: 'R$' },
        { value: 'USD', label: 'USD', symbol: '$' },
        { value: 'EUR', label: 'EUR', symbol: '€' },
      ];

  return (
    <FinanceProfileContext.Provider value={{
      profile,
      loading,
      updateProfile,
      defaultCurrency: profile?.default_currency || 'BRL',
      currencies,
      currencyOptions,
      reloadCurrencies: loadCurrencies,
    }}>
      {children}
    </FinanceProfileContext.Provider>
  );
};
